import { Link } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { CATEGORIES, TOPICS, type Post } from "@/lib/montessori-koulu-posts";
import { CATEGORY_HERO_CARD } from "@/lib/article-heroes-card";

/**
 * Editorial article card for the Montessori-koulu hub.
 * Deliberately limited to three signals — topic, reading time and one
 * optional stage tag — so cards stay calm on mobile.
 */
export function ArticleCard({
  p,
  titleAs = "h3",
  priority = false,
}: {
  p: Post;
  titleAs?: "h3" | "h4";
  priority?: boolean;
}) {
  const Title = titleAs;
  const topicLabel =
    TOPICS.find((t) => t.id === p.topic)?.label ?? CATEGORIES.find((c) => c.id === p.category)?.label ?? p.eyebrow;

  return (
    <Link
      to={p.to}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-26px_rgb(0_0_0/0.16)]"
    >
      <div className="aspect-[16/9] overflow-hidden bg-sand">
        <picture>
          <source type="image/avif" srcSet={CATEGORY_HERO_CARD[p.category].avif} />
          <source type="image/webp" srcSet={CATEGORY_HERO_CARD[p.category].webp} />
          <img
            src={CATEGORY_HERO_CARD[p.category].webp}
            alt=""
            width={720}
            height={405}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </picture>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 md:p-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-clay">
          {topicLabel} <span className="text-muted-foreground">· {p.minutes} min</span>
        </p>
        <Title className="font-display text-xl leading-snug text-foreground text-balance">{p.title}</Title>
        <p className="flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
        <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <span className="truncate">{p.age ?? (p.updatedISO ? `Päivitetty ${formatShort(p.updatedISO)}` : "")}</span>
          <span className="inline-flex shrink-0 items-center gap-1 text-foreground group-hover:text-clay">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            Lue artikkeli →
          </span>
        </div>
      </div>
    </Link>
  );
}

function formatShort(iso: string) {
  const [y, m] = iso.split("-");
  return `${Number(m)}/${y}`;
}